import React, { useEffect, useState } from "react";
import {
  Box, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Menu, MenuItem,
  IconButton, Button, CircularProgress, Tooltip, Divider, TextField,
  useMediaQuery, useTheme, Collapse, Chip, alpha,
} from "@mui/material";
import {
  ArrowDropDown as ArrowDropDownIcon,
  ExpandMore, ExpandLess,
  Description as FileIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ReserveStockLedgerList, ReserveStockLedgerExport } from "../../../Services/Services";

const ReserveStockLedgerTable = () => {
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalIn, setTotalIn] = useState(0);
  const [totalOut, setTotalOut] = useState(0);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [excelLoading, setExcelLoading] = useState(false);
  const [search, setSearch] = useState("");

  // ✅ Date-range state
  const [dateRange, setDateRange] = useState("this_month");
  const [customDates, setCustomDates] = useState({ from: "", to: "" });
  const [menuEl, setMenuEl] = useState(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedLedger, setExpandedLedger] = useState(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDates, setTempCustomDates] = useState({ from: "", to: "" });

  // Type colors using theme
  const getTypeColor = (type) => {
    return type === "in" ? "#7b1fa2" : "#1976d2";
  };

  // ============== Date Helpers ==============
  const formatDisplayDate = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    return `${day}-${m}-${y}`;
  };

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday": {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      }
      case "this_week": {
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      }
      case "previous_week": {
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      }
      case "this_month": {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      }
      case "current_year": {
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      }
      case "financial_year": {
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      }
      default:
        return "";
    }
  };

  // ============== Reserve Display Helpers ==============
  const renderReserveBalance = (balance) => {
    const num = parseFloat(balance) || 0;
    if (num > 0) {
      return (
        <Chip
          icon={<LockIcon sx={{ fontSize: "12px !important" }} />}
          size="small"
          label={`${num} Locked for MOS`}
          sx={{
            bgcolor: alpha("#7b1fa2", 0.12),
            color: "#7b1fa2",
            fontWeight: 700,
            fontSize: "0.75rem",
            height: 24,
            border: `1px solid ${alpha("#7b1fa2", 0.3)}`,
          }}
        />
      );
    } else if (num === 0) {
      return (
        <Chip
          size="small"
          label="0 (All Free)"
          sx={{
            bgcolor: alpha(theme.palette.text.disabled, 0.1),
            color: "text.secondary",
            fontWeight: 600,
            fontSize: "0.75rem",
            height: 24,
          }}
        />
      );
    } else {
      return (
        <Tooltip
          title="Reserve balance minus me nahi hona chahiye. Check karein ki koi extra release to nahi hui."
          arrow
        >
          <Chip
            size="small"
            label={`⚠️ ${Math.abs(num)} Over-released`}
            color="warning"
            sx={{ fontWeight: 700, height: 24, cursor: "help" }}
          />
        </Tooltip>
      );
    }
  };

  const renderReserveMovement = (row) => {
    const isLock = row.transaction_type === "in";
    const qty = row.quantity;
    if (isLock) {
      return (
        <Chip
          icon={<LockIcon sx={{ fontSize: "13px !important" }} />}
          size="small"
          label={`🔒 +${qty} Locked for MOS`}
          sx={{
            bgcolor: alpha("#7b1fa2", 0.12),
            color: "#7b1fa2",
            fontWeight: 700,
            fontSize: "0.72rem",
            height: 22,
            border: `1px solid ${alpha("#7b1fa2", 0.25)}`,
          }}
        />
      );
    } else {
      return (
        <Chip
          icon={<LockOpenIcon sx={{ fontSize: "13px !important" }} />}
          size="small"
          label={`🔓 -${qty} Released / Issued`}
          sx={{
            bgcolor: alpha("#1976d2", 0.12),
            color: "#1976d2",
            fontWeight: 700,
            fontSize: "0.72rem",
            height: 22,
            border: `1px solid ${alpha("#1976d2", 0.25)}`,
          }}
        />
      );
    }
  };

  // ============== Fetch Reserve Stock Ledger ==============
  const fetchReserveStockLedger = async (filters = {}) => {
    setLoading(true);
    try {
      const res = await ReserveStockLedgerList(filters);
      const rows = res?.results?.results || [];
      setLedgerData(Array.isArray(rows) ? rows : []);
      setTotalIn(res.total_in || 0);
      setTotalOut(res.total_out || 0);
      setCurrentBalance(res.current_balance || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch reserve stock ledger");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on filter change
  useEffect(() => {
    const handler = setTimeout(() => {
      const params = {};
      params.date_range = dateRange;
      if (dateRange === "custom" && customDates.from && customDates.to) {
        params.from_date = customDates.from;
        params.to_date = customDates.to;
      }
      if (search) {
        params.search = search;
      }

      fetchReserveStockLedger(params);
    }, 500);

    return () => clearTimeout(handler);
  }, [dateRange, customDates, search]);

  // ============== Excel Export ==============
  const handleExport = async () => {
    setExcelLoading(true);
    try {
      const params = {};
      params.date_range = dateRange;
      if (dateRange === "custom" && customDates.from && customDates.to) {
        params.from_date = customDates.from;
        params.to_date = customDates.to;
      }
      if (search) {
        params.search = search;
      }

      const blob = await ReserveStockLedgerExport(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Reserve_Stock_Ledger_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Excel exported successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  // ============== Reset Filter ==============
  const handleReset = () => {
    setDateRange("this_month");
    setCustomDates({ from: "", to: "" });
    setSearch("");
    setShowCustomDateInputs(false);
  };

  // ============== Custom Date Handlers ==============
  const handleDateRangeSelect = (range) => {
    if (range === "custom") {
      setShowCustomDateInputs(true);
    } else {
      setDateRange(range);
      setShowCustomDateInputs(false);
      setCustomDates({ from: "", to: "" });
      setMenuEl(null);
    }
  };

  const handleApplyCustomDateRange = () => {
    if (tempCustomDates.from && tempCustomDates.to) {
      setCustomDates(tempCustomDates);
      setDateRange("custom");
      setMenuEl(null);
      setShowCustomDateInputs(false);
    }
  };

  const handleCancelCustomDateRange = () => {
    setTempCustomDates({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  return (
    <Box p={2} sx={{ mt: { xs: 4, sm: 2 }, pt: 1, mb: 2 }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ✅ Summary Cards Strip */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Total Locked for MOS (In)
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: "#7b1fa2", lineHeight: 1.2, mt: 0.3 }}
              >
                +{totalIn}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.68rem" }}>
                Locked for Projects
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1,
                borderRadius: "50%",
                bgcolor: alpha("#7b1fa2", 0.1),
                color: "#7b1fa2",
                display: "flex",
              }}
            >
              <LockIcon />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Total Released / Consumed (Out)
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: "#1976d2", lineHeight: 1.2, mt: 0.3 }}
              >
                -{totalOut}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.68rem" }}>
                Freed / Issued to Production
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1,
                borderRadius: "50%",
                bgcolor: alpha("#1976d2", 0.1),
                color: "#1976d2",
                display: "flex",
              }}
            >
              <LockOpenIcon />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Active Locked Stock
              </Typography>
              <Box sx={{ mt: 0.4 }}>
                {renderReserveBalance(currentBalance)}
              </Box>
              <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.68rem", display: "block", mt: 0.3 }}>
                Currently Reserved
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                display: "flex",
              }}
            >
              <InventoryIcon />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Total Reserve Logs
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: theme.palette.text.primary, lineHeight: 1.2, mt: 0.3 }}
              >
                {ledgerData.length}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.68rem" }}>
                Filtered Actions
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.info.main, 0.1),
                color: theme.palette.info.main,
                display: "flex",
              }}
            >
              <FileIcon />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ✅ Date Filter & Search & Export */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4.5}>
          <TextField
            label="Date Range"
            value={
              dateRange === "custom" && customDates.from && customDates.to
                ? `${formatDisplayDate(customDates.from)} to ${formatDisplayDate(customDates.to)}`
                : dateRange === "today"
                ? "Today"
                : dateRange === "yesterday"
                ? "Yesterday"
                : dateRange === "this_week"
                ? "This Week"
                : dateRange === "previous_week"
                ? "Previous Week"
                : dateRange === "this_month"
                ? "This Month"
                : dateRange === "current_year"
                ? "Current Year"
                : dateRange === "financial_year"
                ? "Financial Year"
                : "Select Date Range"
            }
            onClick={(e) => setMenuEl(e.currentTarget)}
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton size="small" edge="end">
                  <ArrowDropDownIcon />
                </IconButton>
              ),
            }}
            sx={{
              cursor: "pointer",
              "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
              "& .MuiInputLabel-root": { fontSize: "0.7rem" },
            }}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={5.5}>
          <TextField
            label="Search (Item, MOS, Remarks)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "text.secondary", mr: 1, fontSize: 20 }} />
              ),
              endAdornment: search ? (
                <IconButton size="small" onClick={() => setSearch("")} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
            sx={{
              "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
              "& .MuiInputLabel-root": { fontSize: "0.7rem" },
            }}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={1.5}>
          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              height: "35px",
              whiteSpace: "nowrap",
              borderRadius: "0",
              fontSize: "0.7rem",
            }}
            fullWidth
          >
            Reset
          </Button>
        </Grid>

        <Grid item xs={12} sm={0.5}>
          <Tooltip title={excelLoading ? "Exporting..." : "Download Excel"} arrow>
            <span>
              <IconButton
                color="success"
                size="small"
                onClick={handleExport}
                disabled={excelLoading}
                sx={{
                  border: `1px solid ${theme.palette.success.main}`,
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.success.main, 0.08),
                  },
                }}
              >
                {excelLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Date Range Menu */}
      <Menu
        anchorEl={menuEl}
        open={Boolean(menuEl)}
        onClose={() => {
          setMenuEl(null);
          setShowCustomDateInputs(false);
          setTempCustomDates({ from: "", to: "" });
        }}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 500,
            bgcolor: theme.palette.background.paper,
          },
        }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setDateRange(range.key);
              setCustomDates({ from: "", to: "" });
              setMenuEl(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2" color="text.primary">
                {range.label}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs
              ? isDark
                ? alpha(theme.palette.primary.main, 0.15)
                : "#e3f2fd"
              : "inherit",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: showCustomDateInputs ? 600 : 400, color: "text.primary" }}
          >
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: isDark ? alpha(theme.palette.common.white, 0.04) : "#f5f5f5",
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Select Date Range
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDates.from}
                onChange={(e) =>
                  setTempCustomDates((prev) => ({ ...prev, from: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: tempCustomDates.to || new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": { height: 40, fontSize: "0.75rem" },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                }}
              />
              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDates.to}
                onChange={(e) =>
                  setTempCustomDates((prev) => ({ ...prev, to: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDates.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": { height: 40, fontSize: "0.75rem" },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                }}
              />
            </Box>

            {tempCustomDates.from && tempCustomDates.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: isDark
                    ? alpha(theme.palette.success.main, 0.15)
                    : "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: theme.palette.success.main,
                  fontWeight: 500,
                }}
              >
                📅 {formatDisplayDate(tempCustomDates.from)} to{" "}
                {formatDisplayDate(tempCustomDates.to)}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDates.from || !tempCustomDates.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>

      {/* ✅ Mobile/Desktop Conditional Rendering */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : ledgerData.length === 0 ? (
        <Box sx={{ textAlign: "center", p: 4 }}>
          <Typography color="text.secondary">No entries found</Typography>
        </Box>
      ) : isMobile ? (
        /* 📱 MOBILE VIEW */
        <Box sx={{ bgcolor: theme.palette.background.paper }}>
          {ledgerData.map((row, index) => {
            const typeColor = getTypeColor(row.transaction_type);
            return (
              <Box key={row.id || index}>
                <Box
                  onClick={() =>
                    setExpandedLedger(expandedLedger === row.id ? null : row.id)
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    cursor: "pointer",
                    bgcolor:
                      index % 2 === 0
                        ? isDark
                          ? alpha(theme.palette.common.white, 0.04)
                          : "#fafafa"
                        : theme.palette.background.paper,
                    "&:active": {
                      bgcolor: isDark
                        ? alpha(theme.palette.primary.main, 0.2)
                        : "#e3f2fd",
                    },
                    borderLeft: `4px solid ${typeColor}`,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "text.disabled",
                          minWidth: 25,
                        }}
                      >
                        #{index + 1}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          flex: 1,
                          color: "text.primary",
                        }}
                      >
                        {row.item_name || "N/A"}
                      </Typography>
                      <Box sx={{ ml: "auto", pr: 1 }}>
                        {renderReserveBalance(row.balance_after)}
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "text.secondary",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>
                        {renderReserveMovement(row)}
                      </span>
                      <span style={{ color: theme.palette.text.disabled }}>•</span>
                      <span style={{ fontSize: "0.7rem" }}>{row.date}</span>
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ ml: 1 }}>
                    {expandedLedger === row.id ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                <Collapse
                  in={expandedLedger === row.id}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor:
                        index % 2 === 0
                          ? isDark
                            ? alpha(theme.palette.common.white, 0.03)
                            : "#f8f9fa"
                          : isDark
                          ? alpha(theme.palette.common.white, 0.02)
                          : "#fafafa",
                      borderLeft: `4px solid ${typeColor}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.8,
                        mb: 1,
                      }}
                    >
                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.disabled",
                            letterSpacing: "0.5px",
                          }}
                        >
                          ITEM NAME
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "text.primary",
                          }}
                        >
                          {row.item_name || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.disabled",
                            letterSpacing: "0.5px",
                          }}
                        >
                          DATE
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "text.primary",
                          }}
                        >
                          {row.date || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.disabled",
                            letterSpacing: "0.5px",
                          }}
                        >
                          RESERVE ACTION
                        </Typography>
                        <Box sx={{ mt: 0.2 }}>
                          {renderReserveMovement(row)}
                        </Box>
                      </Box>

                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.disabled",
                            letterSpacing: "0.5px",
                          }}
                        >
                          SOURCE
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "text.primary",
                          }}
                        >
                          {row.source || "-"}
                        </Typography>
                      </Box>

                      {row.mos_no && (
                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              letterSpacing: "0.5px",
                            }}
                          >
                            MOS NO.
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              color: theme.palette.primary.main,
                            }}
                          >
                            {row.mos_no}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.disabled",
                            letterSpacing: "0.5px",
                          }}
                        >
                          REMARKS
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "text.primary",
                          }}
                        >
                          {row.remarks || "-"}
                        </Typography>
                      </Box>

                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.disabled",
                            letterSpacing: "0.5px",
                          }}
                        >
                          ACTIVE LOCKED BALANCE
                        </Typography>
                        <Box sx={{ mt: 0.3 }}>
                          {renderReserveBalance(row.balance_after)}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
                <Divider sx={{ borderColor: theme.palette.divider }} />
              </Box>
            );
          })}
        </Box>
      ) : (
        /* 💻 DESKTOP VIEW */
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "73vh",
            overflow: "auto",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: 950,
              "& td, & th": {
                textAlign: "center",
                whiteSpace: "nowrap",
                borderBottom: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: isDark ? theme.palette.grey[900] : "#f5f5f5",
                  "& th": {
                    fontWeight: "bold",
                    color: isDark ? theme.palette.primary.light : "#333",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: isDark ? theme.palette.grey[900] : "#f5f5f5",
                  },
                }}
              >
                <TableCell>S.No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Reserve Action</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>MOS No.</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Active Locked Balance</TableCell>
                <TableCell>Remarks</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {ledgerData.map((row, index) => {
                const isLock = row.transaction_type === "in";
                return (
                  <TableRow
                    key={row.id || index}
                    sx={{
                      backgroundColor:
                        index % 2 === 0
                          ? isDark
                            ? alpha(theme.palette.common.white, 0.03)
                            : "#fafafa"
                          : theme.palette.background.paper,
                      "& td": {
                        paddingTop: "4px !important",
                        paddingBottom: "4px !important",
                        fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                      },
                      "&:hover": {
                        backgroundColor: isDark
                          ? theme.palette.grey[800]
                          : "#e3f2fd !important",
                      },
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{row.date}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.item_name}</TableCell>
                    <TableCell>
                      {renderReserveMovement(row)}
                    </TableCell>
                    <TableCell>{row.source}</TableCell>
                    <TableCell>
                      {row.mos_no ? (
                        <Chip
                          size="small"
                          label={row.mos_no}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            fontSize: 11,
                            height: 22,
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={isLock ? `+${row.quantity}` : `-${row.quantity}`}
                        sx={{
                          bgcolor: isLock ? alpha("#7b1fa2", 0.12) : alpha("#1976d2", 0.12),
                          color: isLock ? "#7b1fa2" : "#1976d2",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {renderReserveBalance(row.balance_after)}
                    </TableCell>
                    <TableCell>{row.remarks || "-"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ReserveStockLedgerTable;